
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { TikTokVideo } from "@/types/api";
import { createOrUpdateUser, createCollection, storeVideos, logDownloadAttempt, updateCollectionEmailStatus } from "@/services/supabase/dataService";
import { useAuth } from "@/hooks/useAuth";

export interface DownloadDetails {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export function useCollectionDownload(collectionUrl: string, videos: TikTokVideo[]) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signUp, signIn, user } = useAuth();

  const handleCollectionDownload = async (details: DownloadDetails) => {
    if (!collectionUrl) {
      toast({
        title: "No collection URL found.",
        description: "Please start from a valid TikTok Collection link.",
        variant: "destructive"
      });
      return false;
    }

    setIsSubmitting(true);
    let userId = '';
    let collectionId = '';
    let accountCreated = false;
    
    try {
      // Step 1: Try to create an account or sign in with the provided credentials
      console.log("Step 1: Attempting signup/login with:", details.email);
      
      // First try to sign up the user - this will either create a new account
      // or return an error if the account already exists
      const signUpResult = await signUp(
        details.email,
        details.password,
        details.firstName,
        details.lastName
      );
      
      if (signUpResult.error) {
        // If error is that user already exists, try to sign in
        if (signUpResult.error.message.includes('already registered')) {
          console.log("User already exists, attempting sign in");
          const signInResult = await signIn(details.email, details.password);
          
          if (signInResult.error) {
            console.error("Sign in failed after signup attempt:", signInResult.error);
            toast({
              title: "Authentication error",
              description: "Failed to log in with provided credentials. Please check your email and password.",
              variant: "destructive"
            });
            setIsSubmitting(false);
            return false;
          }
          console.log("Sign in successful for existing user");
        } else {
          // Some other signup error occurred
          console.error("Sign up error:", signUpResult.error);
          toast({
            title: "Account creation failed",
            description: signUpResult.error.message || "Failed to create account",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return false;
        }
      } else {
        console.log("New account created successfully");
        accountCreated = true;
      }
      
      // Step 2: Create/update user in database and handle collection storage
      console.log("Step 2: Starting database operations with user information");
      try {
        // Create or update user in public.users table
        userId = await createOrUpdateUser(
          details.email,
          details.firstName,
          details.lastName
        );
        console.log("User created or updated with ID:", userId);

        // Create collection record
        collectionId = await createCollection(
          userId,
          collectionUrl,
          videos.length,
          videos.reduce((acc, video) => acc + (video.duration || 0), 0)
        );
        console.log("Collection created with ID:", collectionId);

        // Store videos in the collection
        await storeVideos(collectionId, videos);
        console.log("Videos stored successfully");
        
        // Log successful download attempt
        await logDownloadAttempt(
          userId,
          collectionUrl,
          'success',
          null,
          details.email
        );
        console.log("Download attempt logged as success");
      } catch (dbError) {
        console.error('Database operation error:', dbError);
        // Continue with API call even if db operations fail
        if (userId) {
          // Log failed attempt with error message
          try {
            await logDownloadAttempt(
              userId,
              collectionUrl,
              'failed',
              dbError instanceof Error ? dbError.message : 'Unknown database error',
              details.email
            );
          } catch (logError) {
            console.error('Failed to log download attempt:', logError);
          }
        }
        // Don't return here - continue to API call even if DB operations fail
      }

      // Step 3: Make API request to initiate collection download
      console.log("Step 3: Sending API request to download collection:", collectionUrl);
      const response = await fetch("https://tokexcapi.brandsmeetcreators.online/v1/api/tiktok-collection-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: details.firstName,
          lastName: details.lastName,
          email: details.email,
          url: collectionUrl
        })
      });
      
      console.log("API Response status:", response.status);
      const result = await response.json();
      console.log("API Response body:", result);
      
      if (response.ok && result.success) {
        console.log("API request successful:", result);
        toast({
          title: "Request Sent",
          description: "Your download request was sent. Check your inbox for the download link once it is ready.",
          variant: "default"
        });

        if (collectionId) {
          try {
            await updateCollectionEmailStatus(collectionId, true, 'completed');
          } catch (updateError) {
            console.error('Failed to update collection status:', updateError);
          }
        }
        
        return true;
      } else {
        throw new Error(result.message || "Failed to request download");
      }
    } catch (e: any) {
      console.error("API request or processing error:", e);
      toast({
        title: "Error requesting collection download",
        description: e?.message || "API call failed. Please try again later.",
        variant: "destructive"
      });
      
      if (collectionId) {
        try {
          await updateCollectionEmailStatus(
            collectionId, 
            false, 
            'failed',
            e?.message || "API call failed"
          );
        } catch (updateError) {
          console.error('Failed to update collection status:', updateError);
        }
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleCollectionDownload
  };
}
